import React from "react";
import { createPortal } from "react-dom";
import { TimelineStep } from "../../types/timeline";
import { ViewerSettings } from "../../types/settings";
import { withOpacity } from "../../utils/colorUtils";
interface StepDataModalAssignedObject {
  id: string;
  label: string;
  resolved: boolean;
}

interface StepDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  step: TimelineStep | null;
  assignedObjects: StepDataModalAssignedObject[];
  language?: ViewerSettings["language"];
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

function getCopy(language: ViewerSettings["language"]) {
  switch (language) {
    case "es":
      return {
        stepData: "Datos del paso",
        close: "Cerrar modal",
        stepText: "Texto del paso",
        startFrame: "Fotograma inicial",
        endFrame: "Fotograma final",
        assignedObjects: "Objetos asignados",
        unresolved: "Sin resolver",
      };
    case "fr":
      return {
        stepData: "Donn\u00e9es de l\u2019\u00e9tape",
        close: "Fermer la fen\u00eatre",
        stepText: "Texte de l\u2019\u00e9tape",
        startFrame: "Image de d\u00e9but",
        endFrame: "Image de fin",
        assignedObjects: "Objets assign\u00e9s",
        unresolved: "Non r\u00e9solu",
      };
    case "de":
      return {
        stepData: "Schrittdaten",
        close: "Modal schlie\u00dfen",
        stepText: "Schritttext",
        startFrame: "Start-Frame",
        endFrame: "End-Frame",
        assignedObjects: "Zugewiesene Objekte",
        unresolved: "Nicht aufgel\u00f6st",
      };
    case "zh":
      return {
        stepData: "\u6b65\u9aa4\u6570\u636e",
        close: "\u5173\u95ed\u7a97\u53e3",
        stepText: "\u6b65\u9aa4\u6587\u672c",
        startFrame: "\u5f00\u59cb\u5e27",
        endFrame: "\u7ed3\u675f\u5e27",
        assignedObjects: "\u5df2\u5206\u914d\u5bf9\u8c61",
        unresolved: "\u672a\u89e3\u6790",
      };
    case "en":
    default:
      return {
        stepData: "Step Data",
        close: "Close modal",
        stepText: "Step Text",
        startFrame: "Start Frame",
        endFrame: "End Frame",
        assignedObjects: "Assigned Objects",
        unresolved: "Unresolved",
      };
  }
}

export function StepDataModal({
  isOpen,
  onClose,
  step,
  assignedObjects,
  language = "en",
  backgroundColor = "rgba(55, 55, 55, 0.85)",
  backdropBlur = "24px",
  borderColor = "rgba(255, 255, 255, 0.12)",
  boxShadow = "0 8px 32px 0 rgba(0, 0, 0, 0.4)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
}: StepDataModalProps) {
  const copy = getCopy(language);

  if (!isOpen || !step) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 210,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        fontFamily,
        pointerEvents: "none",
      }}
      role="presentation"
      data-oid=":olrfvg"
    >
      <div
        style={{
          width: "min(960px, calc(100vw - 3rem))",
          marginBottom: "2rem",
          borderRadius: "1.5rem",
          overflow: "hidden",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          padding: "1.5rem",
          pointerEvents: "auto",
        }}
        role="dialog"
        aria-modal="false"
        aria-labelledby="step-data-modal-title"
        data-oid="lirxjx5"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "1rem",
          }}
          data-oid="174km24"
        >
          <div data-oid="8kl5anv">
            <div
              style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: `${accentColor}CC`,
                marginBottom: "0.5rem",
              }}
              data-oid="ou3rf._"
            >
              {copy.stepData}
            </div>
            <h2
              id="step-data-modal-title"
              style={{
                margin: 0,
                fontSize: "1.125rem",
                color: primaryTextColor,
              }}
              data-oid="8nbn.fn"
            >
              {step.name}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label={copy.close}
            style={{
              width: "2.5rem",
              height: "2.5rem",
              borderRadius: "9999px",
              border: "none",
              background: "transparent",
              color: secondaryTextColor,
              cursor: "pointer",
            }}
            data-oid="6ylcalj"
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "1.25rem", fontWeight: 300 }}
              data-oid="q8sp:5-"
            >
              close
            </span>
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(0, 2fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr)",
            gap: "1rem",
            marginBottom: "1.25rem",
          }}
          data-oid="th2757p"
        >
          <div data-oid="ws6-viv">
            <div
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: secondaryTextColor,
                marginBottom: "0.4rem",
              }}
              data-oid="icnjdbm"
            >
              {copy.stepText}
            </div>
            <div
              style={{ color: primaryTextColor, lineHeight: 1.6 }}
              data-oid="0rf.h6r"
            >
              {step.stepText || "\u2014"}
            </div>
          </div>
          <div data-oid="gquyxzi">
            <div
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: secondaryTextColor,
                marginBottom: "0.4rem",
              }}
              data-oid="1jvjj7-"
            >
              {copy.startFrame}
            </div>
            <div
              style={{
                color: primaryTextColor,
                fontFamily:
                  "'JetBrains Mono', 'Roboto Mono', 'SFMono-Regular', Consolas, monospace",
              }}
              data-oid=".qdx-1l"
            >
              {step.startFrame}
            </div>
          </div>
          <div data-oid="-1sca1c">
            <div
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: secondaryTextColor,
                marginBottom: "0.4rem",
              }}
              data-oid="zxk0n99"
            >
              {copy.endFrame}
            </div>
            <div
              style={{
                color: primaryTextColor,
                fontFamily:
                  "'JetBrains Mono', 'Roboto Mono', 'SFMono-Regular', Consolas, monospace",
              }}
              data-oid="3rybks1"
            >
              {step.endFrame}
            </div>
          </div>
        </div>

        <div data-oid="_4dhfy5">
          <div
            style={{
              fontSize: "0.7rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: secondaryTextColor,
              marginBottom: "0.65rem",
            }}
            data-oid="wpm4uuj"
          >
            {copy.assignedObjects}
          </div>
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}
            data-oid="5a6rvem"
          >
            {assignedObjects.length > 0 ? (
              assignedObjects.map((object) => (
                <span
                  key={`${object.id}-${object.resolved ? "resolved" : "unresolved"}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.4rem",
                    borderRadius: "9999px",
                    border: `1px solid ${object.resolved ? withOpacity(accentColor, 0.35) : "rgba(244, 63, 94, 0.3)"}`,
                    background: object.resolved
                      ? withOpacity(accentColor, 0.12)
                      : "rgba(244, 63, 94, 0.12)",
                    color: object.resolved
                      ? primaryTextColor
                      : secondaryTextColor,
                    padding: "0.4rem 0.7rem",
                    fontSize: "0.75rem",
                  }}
                  data-oid="_v_h.j."
                >
                  <span data-oid="0-ctxik">{object.label}</span>
                  {!object.resolved ? (
                    <span
                      style={{ fontSize: "0.65rem", opacity: 0.8 }}
                      data-oid="zh32z-x"
                    >
                      {copy.unresolved}
                    </span>
                  ) : null}
                </span>
              ))
            ) : (
              <span
                style={{ color: secondaryTextColor, fontSize: "0.875rem" }}
                data-oid="9sj1hc."
              >
                \u2014
              </span>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
